import { Injectable } from '@angular/core';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { UserService } from '../../_services/data-services/user.service';
import { SURVEY_CONFIG, SurveyConfig } from './survey.config';

@Injectable({ providedIn: 'root' })
export class SurveyPopupService {
  constructor(
    private userService: UserService,
    private environmentConfigService: EnvironmentConfigService
  ) {}

  get config(): SurveyConfig {
    return SURVEY_CONFIG;
  }

  checkAndAdvanceSession(): boolean {
    try {
      if (
        !this.environmentConfigService.surveyEnabled ||
        !SURVEY_CONFIG.scriptSrc
      ) {
        return false;
      }

      if (this.userService.isImpersonated() || this.userService.isInternal()) {
        return false;
      }

      const dismissKey = this.userScopedKey(
        StorageKeyConstants.surveyDismissedPrefix + SURVEY_CONFIG.surveyId
      );
      const resumeAt = localStorage.getItem(dismissKey);
      if (resumeAt) {
        const resumeTs = new Date(resumeAt).getTime();
        if (isNaN(resumeTs)) {
          localStorage.removeItem(dismissKey);
        } else if (Date.now() < resumeTs) {
          return false;
        }
      }

      // Increment session counter and gate until minSessionsBeforeShow is reached.
      // Once the user has interacted (cooldown active), this gate is skipped above.
      const sessionKey = this.userScopedKey(
        StorageKeyConstants.surveySessionCount
      );
      const sessionCount = parseInt(
        localStorage.getItem(sessionKey) ?? '0',
        10
      );
      const nextCount = sessionCount + 1;
      localStorage.setItem(sessionKey, String(nextCount));
      return nextCount >= SURVEY_CONFIG.minSessionsBeforeShow;
    } catch {
      return false;
    }
  }

  markDismissed(): void {
    try {
      const dismissKey = this.userScopedKey(
        StorageKeyConstants.surveyDismissedPrefix + SURVEY_CONFIG.surveyId
      );
      const resumeAt = new Date(
        Date.now() + SURVEY_CONFIG.cooldownDays * 86400000
      ).toISOString();
      localStorage.setItem(dismissKey, resumeAt);
    } catch {
      // storage unavailable — degrade silently
    }
  }

  // Keys are scoped to the logged-in user so cooldown and session count
  // never bleed across different accounts on a shared machine.
  private userScopedKey(base: string): string {
    const userId = this.userService.getCurrentUserInfo()?.userId;
    return userId ? `${base}_${userId}` : base;
  }
}
